/**
 * Statutory Reports Page
 * SSNIT, PAYE, Tier 2/3 compliance reports for Ghana
 */

import React, { useState, useEffect } from 'react'
import { FileText, Download, Calendar, CheckCircle, AlertCircle, FileSpreadsheet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AdminUser } from '@/lib/authService'
import { formatCurrency } from '@/lib/currency'
import { supabase } from '@/lib/supabase'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

interface StatutoryReportsProps {
  user: AdminUser | null
}

interface SSNITContribution {
  id: string
  contribution_month: number
  contribution_year: number
  total_employee_contribution: number
  total_employer_contribution: number
  total_contribution: number
  payment_status: string
  payment_date?: string
}

interface PAYERecord {
  id: string
  tax_month: number
  tax_year: number
  total_paye: number
  payment_status: string
  payment_date?: string
  filed_with_gra: boolean
}

interface PensionContribution {
  id: string
  contribution_month: number
  contribution_year: number
  tier2_employee: number
  tier2_employer: number
  tier2_total: number
  tier3_employee: number
  tier3_total: number
  payment_status: string
}

export default function StatutoryReports({ user }: StatutoryReportsProps) {
  const { features, limits, loading: subscriptionLoading } = useSubscription(user?.churchId)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [ssnitData, setSSNITData] = useState<SSNITContribution[]>([])
  const [payeData, setPAYEData] = useState<PAYERecord[]>([])
  const [pensionData, setPensionData] = useState<PensionContribution[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user?.churchId) {
      loadStatutoryData()
    }
  }, [user, selectedYear])

  async function loadStatutoryData() {
    if (!user?.churchId) return

    setLoading(true)
    try {
      // Load SSNIT contributions
      const { data: ssnit } = await supabase
        .from('ssnit_contributions')
        .select('*')
        .eq('church_id', user.churchId)
        .eq('contribution_year', selectedYear)
        .order('contribution_month', { ascending: false })

      // Load PAYE records
      const { data: paye } = await supabase
        .from('paye_tax_ledger')
        .select('*')
        .eq('church_id', user.churchId)
        .eq('tax_year', selectedYear)
        .order('tax_month', { ascending: false })

      // Load pension contributions
      const { data: pension } = await supabase
        .from('pension_contributions')
        .select('*')
        .eq('church_id', user.churchId)
        .eq('contribution_year', selectedYear)
        .order('contribution_month', { ascending: false })

      setSSNITData(ssnit || [])
      setPAYEData(paye || [])
      setPensionData(pension || [])
    } catch (error) {
      console.error('Error loading statutory data:', error)
    } finally {
      setLoading(false)
    }
  }

  function getMonthName(month: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
    return months[month - 1]
  }

  function exportSSNITReport() {
    // Generate CSV for SSNIT report
    const headers = ['Month', 'Year', 'Employee Contribution (5.5%)', 'Employer Contribution (13%)', 'Total Contribution (18.5%)', 'Status', 'Payment Date']
    const rows = ssnitData.map(record => [
      getMonthName(record.contribution_month),
      record.contribution_year,
      record.total_employee_contribution.toFixed(2),
      record.total_employer_contribution.toFixed(2),
      record.total_contribution.toFixed(2),
      record.payment_status,
      record.payment_date || 'Not Paid'
    ])

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `SSNIT_Report_${selectedYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function exportPAYEReport() {
    // Generate CSV for PAYE report
    const headers = ['Month', 'Year', 'Total PAYE Tax', 'Status', 'Payment Date', 'Filed with GRA']
    const rows = payeData.map(record => [
      getMonthName(record.tax_month),
      record.tax_year,
      record.total_paye.toFixed(2),
      record.payment_status,
      record.payment_date || 'Not Paid',
      record.filed_with_gra ? 'Yes' : 'No'
    ])

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `PAYE_Report_${selectedYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function exportPensionReport() {
    // Generate CSV for Pension report
    const headers = ['Month', 'Year', 'Tier 2 Employee (5%)', 'Tier 2 Employer (5%)', 'Tier 2 Total (10%)', 'Tier 3 Total', 'Status']
    const rows = pensionData.map(record => [
      getMonthName(record.contribution_month),
      record.contribution_year,
      record.tier2_employee.toFixed(2),
      record.tier2_employer.toFixed(2),
      record.tier2_total.toFixed(2),
      record.tier3_total.toFixed(2),
      record.payment_status
    ])

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Pension_Report_${selectedYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Calculate totals
  const ssnitTotals = ssnitData.reduce((acc, record) => ({
    employee: acc.employee + record.total_employee_contribution,
    employer: acc.employer + record.total_employer_contribution,
    total: acc.total + record.total_contribution
  }), { employee: 0, employer: 0, total: 0 })

  const payeTotals = payeData.reduce((acc, record) => acc + record.total_paye, 0)

  const pensionTotals = pensionData.reduce((acc, record) => ({
    tier2Employee: acc.tier2Employee + record.tier2_employee,
    tier2Employer: acc.tier2Employer + record.tier2_employer,
    tier2Total: acc.tier2Total + record.tier2_total,
    tier3: acc.tier3 + record.tier3_total
  }), { tier2Employee: 0, tier2Employer: 0, tier2Total: 0, tier3: 0 })

  if (loading || subscriptionLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    )
  }

  if (!features.hrPayroll) {
    return (
      <FeatureRestricted
        featureName="Statutory Reports"
        description="Generate compliance reports for tax authorities including PAYE, NSSF, NHIF, and other statutory requirements."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="leadership"
        icon={<FileSpreadsheet className="h-10 w-10 text-white" />}
      />
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Statutory Reports</h1>
            <p className="text-sm text-muted-foreground">
              SSNIT, PAYE, and Pension compliance reports for Ghana
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label>Year:</Label>
            <Select
              value={selectedYear.toString()}
              onValueChange={(value) => setSelectedYear(parseInt(value))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <Tabs defaultValue="ssnit" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="ssnit">SSNIT Contributions</TabsTrigger>
            <TabsTrigger value="paye">PAYE Tax</TabsTrigger>
            <TabsTrigger value="pension">Tier 2/3 Pensions</TabsTrigger>
          </TabsList>

          {/* SSNIT Report */}
          <TabsContent value="ssnit" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">SSNIT Contribution Report {selectedYear}</h2>
                <p className="text-sm text-muted-foreground">
                  Social Security and National Insurance Trust monthly contributions
                </p>
              </div>
              <Button onClick={exportSSNITReport}>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Employee (5.5%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(ssnitTotals.employee, 'GHS')}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Year-to-date
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Employer (13%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(ssnitTotals.employer, 'GHS')}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Year-to-date
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Total (18.5%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(ssnitTotals.total, 'GHS')}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Year-to-date
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* SSNIT Table */}
            <Card>
              <CardContent className="pt-6">
                <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Employee (5.5%)</TableHead>
                        <TableHead className="text-right">Employer (13%)</TableHead>
                        <TableHead className="text-right">Total (18.5%)</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ssnitData.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">
                            {getMonthName(record.contribution_month)} {record.contribution_year}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(record.total_employee_contribution, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(record.total_employer_contribution, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(record.total_contribution, 'GHS')}
                          </TableCell>
                          <TableCell>
                            <Badge variant={record.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {record.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {record.payment_date ? new Date(record.payment_date).toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAYE Report */}
          <TabsContent value="paye" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">PAYE Tax Report {selectedYear}</h2>
                <p className="text-sm text-muted-foreground">
                  Pay As You Earn income tax for Ghana Revenue Authority
                </p>
              </div>
              <Button onClick={exportPAYEReport}>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>

            {/* Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle>Year-to-Date PAYE</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {formatCurrency(payeTotals, 'GHS')}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Total PAYE tax deducted for {selectedYear}
                </p>
              </CardContent>
            </Card>

            {/* PAYE Table */}
            <Card>
              <CardContent className="pt-6">
                <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">PAYE Tax</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment Date</TableHead>
                        <TableHead>GRA Filing</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payeData.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">
                            {getMonthName(record.tax_month)} {record.tax_year}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(record.total_paye, 'GHS')}
                          </TableCell>
                          <TableCell>
                            <Badge variant={record.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {record.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {record.payment_date ? new Date(record.payment_date).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {record.filed_with_gra ? (
                              <Badge variant="default">
                                <CheckCircle className="mr-1 h-3 w-3" />
                                Filed
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <AlertCircle className="mr-1 h-3 w-3" />
                                Not Filed
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pension Report */}
          <TabsContent value="pension" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Pension Contribution Report {selectedYear}</h2>
                <p className="text-sm text-muted-foreground">
                  Tier 2 (mandatory) and Tier 3 (voluntary) pension contributions
                </p>
              </div>
              <Button onClick={exportPensionReport}>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Tier 2 Employee (5%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(pensionTotals.tier2Employee, 'GHS')}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Tier 2 Employer (5%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(pensionTotals.tier2Employer, 'GHS')}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Tier 2 Total (10%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(pensionTotals.tier2Total, 'GHS')}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Tier 3 (Voluntary)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(pensionTotals.tier3, 'GHS')}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pension Table */}
            <Card>
              <CardContent className="pt-6">
                <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Tier 2 Employee</TableHead>
                        <TableHead className="text-right">Tier 2 Employer</TableHead>
                        <TableHead className="text-right">Tier 2 Total</TableHead>
                        <TableHead className="text-right">Tier 3</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pensionData.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">
                            {getMonthName(record.contribution_month)} {record.contribution_year}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(record.tier2_employee, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(record.tier2_employer, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(record.tier2_total, 'GHS')}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(record.tier3_total, 'GHS')}
                          </TableCell>
                          <TableCell>
                            <Badge variant={record.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {record.payment_status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
